import { useState } from 'react'
import type { ReactNode } from 'react'
import {
  type Condition,
  type CustomAgentConfig,
  type CustomAgentTrigger,
  type TriggerEvent,
  type UpdateCustomAgentConfigPayload
} from '@axilon-apps/domains-ontology-ui-state'

import { ConditionBuilder } from '../triggers/ConditionBuilder'
import { collectUnresolvedTagIds, conditionHasTagId } from '../triggers/condition-tag-ids'
import { newRcaTrigger, normalizeTriggerFields } from '../triggers/condition-defaults'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { InlineAlert } from '../../pages/ConfigurePage/components/DetailStates'

/**
 * Editable state for a custom agent's trigger surface. Everything here maps
 * onto `PATCH /custom-agents/configs/{id}` fields; the mission prompt, cron,
 * and output schema are intentionally not part of this form.
 */
export interface CustomAgentTriggersFormValues {
  triggers: CustomAgentTrigger[]
  /** `undefined` = leave the persisted value alone; `null` = legacy level dispatch. */
  trigger_events: TriggerEvent[] | null | undefined
  /** Cooldown between condition-fired runs, in seconds (persisted as ms). */
  cooldown_seconds: number
}

export interface CustomAgentTriggersFormProps {
  initialValue: CustomAgentConfig
  onSubmit: (patch: UpdateCustomAgentConfigPayload) => Promise<void> | void
  onCancel: () => void
  busy?: boolean
  error?: string | null
}

type TriggerEventsChoice = 'legacy' | 'activated' | 'cleared' | 'both' | 'none'

export function triggerEventsToChoice(
  events: TriggerEvent[] | null | undefined
): TriggerEventsChoice {
  if (events == null) return 'legacy'
  if (events.length === 0) return 'none'
  const hasActivated = events.includes('activated')
  const hasCleared = events.includes('cleared')
  if (hasActivated && hasCleared) return 'both'
  return hasCleared ? 'cleared' : 'activated'
}

export function choiceToTriggerEvents(choice: TriggerEventsChoice): TriggerEvent[] | null {
  switch (choice) {
    case 'legacy':
      return null
    case 'both':
      return ['activated', 'cleared']
    case 'activated':
      return ['activated']
    case 'cleared':
      return ['cleared']
    case 'none':
      return []
  }
}

export function initialCustomAgentTriggersForm(
  config: CustomAgentConfig
): CustomAgentTriggersFormValues {
  return {
    triggers: (config.triggers ?? []).map((t) => normalizeTriggerFields(t)),
    trigger_events: config.trigger_events ?? null,
    cooldown_seconds: Math.max(0, Math.round((config.trigger_cooldown_ms ?? 0) / 1000))
  }
}

/**
 * Count of leaves / expr sources whose `tag_id` the proxy could not resolve.
 * The proxy rejects the whole PATCH (`CUSTOM_AGENT_CONFIG_TAG_RESOLVE_FAILED`)
 * while any remain, even if the operator only changed a threshold, so Save is
 * gated on this reaching zero.
 */
export function countUnresolvedTriggerTags(trigger: CustomAgentTrigger): number {
  const ids = new Set<string>()
  collectUnresolvedTagIds(trigger.condition, ids)
  return ids.size
}

/**
 * Pure PATCH-body builder. Triggers stay in the form `Condition` shape here;
 * the state layer's `denormalizeUpdateCustomAgentConfigPayload` converts to
 * the proxy wire shape on send. An empty trigger list is a legitimate save
 * (removes every trigger; the agent may still be cron-driven).
 */
export function buildCustomAgentTriggersPatch(
  form: CustomAgentTriggersFormValues
): UpdateCustomAgentConfigPayload {
  return {
    triggers: form.triggers.map((trigger) => normalizeTriggerFields(trigger)),
    trigger_events: form.trigger_events,
    trigger_cooldown_ms: Math.max(0, Math.round(form.cooldown_seconds)) * 1000
  }
}

export function CustomAgentTriggersForm({
  initialValue,
  onSubmit,
  onCancel,
  busy,
  error
}: CustomAgentTriggersFormProps) {
  const [form, setForm] = useState<CustomAgentTriggersFormValues>(() =>
    initialCustomAgentTriggersForm(initialValue)
  )
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const allTriggersHaveTag = form.triggers.every((t) => conditionHasTagId(t.condition))
  const unresolvedTotal = form.triggers.reduce((n, t) => n + countUnresolvedTriggerTags(t), 0)
  const canSubmit = allTriggersHaveTag && unresolvedTotal === 0 && !busy
  const showErr = submitAttempted

  const updateTrigger = (index: number, patch: Partial<CustomAgentTrigger>) =>
    setForm((f) => {
      const triggers = f.triggers.slice()
      triggers[index] = { ...triggers[index], ...patch }
      return { ...f, triggers }
    })

  const handleSubmit = () => {
    setSubmitAttempted(true)
    if (!canSubmit) return
    void onSubmit(buildCustomAgentTriggersPatch(form))
  }

  return (
    <div className="flex flex-col gap-5 rounded-xl border border-line bg-card p-4 shadow-sm">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Trigger events"
          htmlFor="custom-agent-trigger-events"
          hint="Which persisted trigger transitions dispatch a run. Legacy keeps level dispatch for older agents."
        >
          <Select
            value={triggerEventsToChoice(form.trigger_events)}
            onValueChange={(value) =>
              setForm((f) => ({
                ...f,
                trigger_events: choiceToTriggerEvents(value as TriggerEventsChoice)
              }))
            }
            disabled={busy}
          >
            <SelectTrigger id="custom-agent-trigger-events" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="legacy">Legacy level dispatch</SelectItem>
              <SelectItem value="activated">Activation</SelectItem>
              <SelectItem value="cleared">Return to normal</SelectItem>
              <SelectItem value="both">Activation and return to normal</SelectItem>
              <SelectItem value="none">No edge dispatch</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field
          label="Cooldown between triggered runs (seconds)"
          htmlFor="custom-agent-trigger-cooldown"
          hint="Minimum time between condition-fired runs. 0 disables the cooldown."
        >
          <Input
            id="custom-agent-trigger-cooldown"
            type="number"
            min={0}
            value={form.cooldown_seconds}
            disabled={busy}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                cooldown_seconds: Math.max(0, Number(e.target.value) || 0)
              }))
            }
          />
        </Field>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h3 className="m-0 text-sm font-semibold text-ink">Triggers</h3>
            <p className="mt-0.5 m-0 text-xs text-ink-4">
              Every trigger is tracked independently; all active triggers count. Removing every
              trigger leaves the agent on its schedule only.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() =>
              setForm((f) => ({
                ...f,
                triggers: [...f.triggers, newRcaTrigger({ name: '', purpose: '' })]
              }))
            }
          >
            + Add trigger
          </Button>
        </div>

        {form.triggers.length === 0 && (
          <p className="m-0 rounded-lg border border-dashed border-line px-4 py-3 text-sm text-ink-3">
            No condition triggers. Add one to investigate when a tag condition is met.
          </p>
        )}

        <div className="flex flex-col gap-3">
          {form.triggers.map((trigger, i) => (
            <div
              key={trigger.id}
              className="flex flex-col gap-3 rounded-lg border border-line bg-bg-elev/40 p-4"
              data-testid={`custom-agent-trigger-${i}`}
            >
              <div className="grid gap-3 sm:grid-cols-[1fr_2fr_auto] sm:items-end">
                <Field label="Name" htmlFor={`custom-agent-trigger-${i}-name`}>
                  <Input
                    id={`custom-agent-trigger-${i}-name`}
                    value={trigger.name ?? ''}
                    disabled={busy}
                    onChange={(e) => updateTrigger(i, { name: e.target.value })}
                    placeholder="Cycle stall"
                  />
                </Field>
                <Field label="Purpose" htmlFor={`custom-agent-trigger-${i}-purpose`}>
                  <Input
                    id={`custom-agent-trigger-${i}-purpose`}
                    value={trigger.purpose ?? ''}
                    disabled={busy}
                    onChange={(e) => updateTrigger(i, { purpose: e.target.value })}
                    placeholder="Caps applied stops increasing during a run"
                  />
                </Field>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  aria-label={`Remove trigger ${i + 1}`}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      triggers: f.triggers.filter((_, j) => j !== i)
                    }))
                  }
                >
                  Remove
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label="Activation delay (seconds)"
                  htmlFor={`custom-agent-trigger-${i}-on-delay`}
                  hint="Condition must remain true for this long."
                >
                  <Input
                    id={`custom-agent-trigger-${i}-on-delay`}
                    type="number"
                    min={0}
                    value={trigger.on_delay_seconds ?? ''}
                    disabled={busy}
                    onChange={(e) =>
                      updateTrigger(i, {
                        on_delay_seconds:
                          e.target.value === '' ? undefined : Math.max(0, Number(e.target.value))
                      })
                    }
                    placeholder="0"
                  />
                </Field>
                <Field
                  label="Clear delay (seconds)"
                  htmlFor={`custom-agent-trigger-${i}-off-delay`}
                  hint="Condition must remain false for this long."
                >
                  <Input
                    id={`custom-agent-trigger-${i}-off-delay`}
                    type="number"
                    min={0}
                    value={trigger.off_delay_seconds ?? ''}
                    disabled={busy}
                    onChange={(e) =>
                      updateTrigger(i, {
                        off_delay_seconds:
                          e.target.value === '' ? undefined : Math.max(0, Number(e.target.value))
                      })
                    }
                    placeholder="0"
                  />
                </Field>
              </div>
              <ConditionBuilder
                condition={trigger.condition}
                onChange={(next: Condition) => updateTrigger(i, { condition: next })}
              />
              {showErr && !conditionHasTagId(trigger.condition) && (
                <p className="m-0 text-xs text-st-down">
                  Pick a tag for at least one condition in this trigger.
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {unresolvedTotal > 0 && (
        <InlineAlert kind="warn">
          <span className="font-medium">
            {unresolvedTotal} tag reference{unresolvedTotal === 1 ? '' : 's'} no longer resolve
            {unresolvedTotal === 1 ? 's' : ''}
          </span>
          {
            ' — the registry tags this agent was built against are gone (a re-ingest or reset replaces registry ids). Re-pick each highlighted tag before saving; the proxy rejects the whole save while any remain.'
          }
        </InlineAlert>
      )}

      {error && <InlineAlert kind="down">{error}</InlineAlert>}

      <div className="flex flex-row gap-2">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={busy || unresolvedTotal > 0}
          title={unresolvedTotal > 0 ? 'Re-pick the highlighted tags before saving.' : undefined}
        >
          {busy ? 'Saving…' : 'Save triggers'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

function Field({
  label,
  htmlFor,
  hint,
  children
}: {
  label: string
  htmlFor?: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && <p className="m-0 text-xs text-ink-3">{hint}</p>}
    </div>
  )
}
